<template>
  <section>
    <DeleteModal
      :show="showDeleteModal"
      :reportId="selectedReportId"
      @delete="deleteReportRow"
      @cancel="closeDeleteModal"
    >
      <template v-slot:header>Delete saved report</template>
      <template v-slot:content>
        Are you sure you want to delete {{ selectedReportName }}?
        <br />
        This action cannot be undone
      </template>
      <template v-slot:cancel>Cancel</template>
      <template v-slot:complete>Yes, delete report</template>
    </DeleteModal>
    <DataTableV01
      :key="selectedReportId"
      showExpand
      disableFooterItemsPerPage
      item-key="reportId"
      :items="tableData"
      :headers="tableColumns"
      :filter-key="searchTerm"
      :search="searchTerm"
      :actions="tableActions"
      sort-by="dateCreated"
      :sort-desc="true"
    >
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          Details:
          <BadgeV02
            label
            outlined
            v-for="metric in getMetricsTags(item)"
            :key="metric"
          >{{ metric }}</BadgeV02>
          <BadgeV02
            label
            outlined
            v-for="dimension in getDimensionsTags(item)"
            :key="dimension"
          >{{ dimension }}</BadgeV02>

          <div v-if="item.filterJson">
            <FilterBadge
              v-for="filter in item.filterJson.filters"
              :key="filter.key"
              :filter="filter"
              type="displayOnly"
            ></FilterBadge>
          </div>
        </td>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip
          v-for="(action, index) in tableActions"
          bottom
          :key="action.tooltip"
        >
          <template v-slot:activator="{ on }">
            <Icon
              v-on="on"
              small
              :class="{ 'mr-2': index !== tableActions.length - 1 }"
              @click="action.onClick(item)"
            >
              {{ action.icon }}
            </Icon>
          </template>
          <span>{{ action.tooltip }}</span>
        </v-tooltip>
      </template>
    </DataTableV01>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import { format as formatDate } from 'date-fns';
import {
  BadgeV02,
  DataTableV01,
  Icon,
} from '@mikmak/components';
import FilterBadge from '../Common/Badges/FilterBadge/FilterBadge.vue';
import {
  getAllReports,
  deleteReport,
  getDimensionsTagsFromReport,
  getMetricsTagsFromReport,
} from '../../../services/dashboard/scheduledReportService';
import DeleteModal from '../Common/DeleteModal';

export default {
  components: {
    BadgeV02,
    DataTableV01,
    FilterBadge,
    DeleteModal,
    Icon,
  },
  props: {
    searchTerm: {
      type: String,
      default: '',
    },
    selectedReportId: {
      type: String,
      default: '',
    },
    error: {
      type: String,
      default: '',
    },
  },
  mounted() {
    this.getReportsApiData();
  },
  updated() {
    this.colorizeScheduleValues();
  },
  data() {
    return {
      showDeleteModal: false,
      tableColumns: [
        { text: 'Report Name', value: 'name' },
        { text: 'Brand', value: 'brand' },
        { text: 'Date Created', value: 'dateCreated' },
        { text: 'Schedule', value: 'schedule' },
        { text: 'Action', value: 'actions' },
      ],
      tableActions: [
        {
          icon: 'mdi-clock-time-four-outline',
          tooltip: 'Edit Report',
          onClick: this.showScheduleReportSlideOut,
        },
        {
          icon: 'mdi-delete',
          tooltip: 'Delete Report',
          onClick: this.openDeleteModal,
        },
      ],
      tableData: [],
    };
  },
  computed: {
    ...mapState({
      allReportFields: state => state.dashboard.availableReportFields.allReportFields,
    }),
  },
  methods: {
    getDimensionsTags(report) {
      return getDimensionsTagsFromReport(report, this.allReportFields);
    },
    getMetricsTags(report) {
      return getMetricsTagsFromReport(report, this.allReportFields);
    },
    async getReportsApiData() {
      try {
        const token = await this.$auth.getTokenSilently();
        const reports = await getAllReports(token);

        if (reports && reports.data && reports.data.length) {
          this.validateReportSchedules(reports.data);
          this.formatCreatedAt(reports.data);

          this.tableData = reports.data;
        }
      } catch (e) {
        if (e.status === 404) {
          this.tableData = [];
        } else {
          this.toggleErrorMessage('We are having trouble retrieving reports, please try again');
        }
      }
    },
    async deleteReportRow() {
      try {
        const token = await this.$auth.getTokenSilently();
        const wasDeleted = await deleteReport(this.selectedReportId, token);

        if (!wasDeleted) throw new Error();

        this.$store.dispatch('setUserSavedReports', token);

        this.tableData = this.tableData
          .filter(report => report.reportId !== this.selectedReportId);

        this.closeDeleteModal();
      } catch (e) {
        this.toggleErrorMessage('Could not delete report. Please try again.');
      }
    },
    formatCreatedAt(reports) {
      reports.forEach(report => {
        if (report.dateCreated) {
          report.dateCreated = formatDate(new Date(report.dateCreated), 'MM/dd/yyyy');
        }
      });
    },
    validateReportSchedules(reports) {
      const ignoredSchedules = ['quarterly', 'daily', 'weekly', 'monthly', 'yearly', 'biweekly'];

      reports.forEach(report => {
        if (report.schedule && report.startDate) {
          if (ignoredSchedules.includes(report.schedule)) {
            return;
          }

          if (!report.schedule.includes(' ')) {
            const startDate = new Date(report.startDate).getTime();
            const options = {
              daily: '9:00AM EST',
              weekly: formatDate(startDate, 'EEEE'),
              biweekly: formatDate(startDate, 'EEEE'),
              monthly: formatDate(startDate, 'do'),
              yearly: formatDate(startDate, 'LLL do'),
            };

            report.schedule = (`${report.schedule}, ${options[report.schedule]}`);
          }
        } else {
          report.schedule = 'not scheduled';
        }
      });
    },
    colorizeScheduleValues() {
      const values = [...document.querySelectorAll('td.text-start:nth-of-type(5)')];
      const notScheduled = values.filter(value => {
        return value.textContent === 'not scheduled';
      });
      const scheduled = values.filter(value => {
        return value.textContent && value.textContent !== 'not scheduled';
      });

      notScheduled.forEach(element => {
        const span = this.createSpanWithText('not-scheduled', element.textContent);

        element.textContent = '';
        element.appendChild(span);
      });

      scheduled.forEach(element => {
        const span = this.createSpanWithText('scheduled', element.textContent);

        element.textContent = '';
        element.appendChild(span);
      });
    },
    createSpanWithText(classes, text) {
      const span = document.createElement('span');

      span.textContent = text;
      span.classList.add(classes);

      span.onclick = (evt) => {
        const reports = this.tableData;
        const reportName = evt.target
          .closest('tr')
          .querySelector('.text-start:nth-of-type(2)')
          .innerText;
        const report = reports
          .find(report => report.name === reportName) || {};
        const reportId = report.reportId || '';

        this.showScheduleReportSlideOut({ reportId });
      };

      return span;
    },
    showScheduleReportSlideOut(selectedReport) {
      this.selectedReportName = selectedReport.reportName;

      this.$emit('changeScheduleReportId', selectedReport.reportId);
      this.$emit('toggleSlideOut', true);
    },
    openDeleteModal({ reportId, name }) {
      this.selectedReportName = name;
      this.showDeleteModal = true;

      this.$emit('changeScheduleReportId', reportId);
    },
    closeDeleteModal() {
      this.$emit('changeScheduleReportId', '');
      this.showDeleteModal = false;
    },
    updateScheduleData(reportData) {
      this.tableData = this.tableData.map(report => {
        if (report.reportId === reportData.reportId) {
          this.validateReportSchedules([reportData]);
          report = { ...report, ...reportData };
        }

        return report;
      });
    },
    toggleErrorMessage(message) {
      this.$emit('updateErrorMessage', message);

      setTimeout(() => {
        this.$emit('updateErrorMessage', '');
      }, 3000);
    },
  },
};
</script>

<style>
.first-data-child {
  color: #4369F4;
}

.scheduled,
.not-scheduled {
  font-size: 10px;
  padding: 5px;
  border-radius: 3px;
  cursor: pointer;
  text-transform: uppercase;
}

.scheduled {
  background-color: #AEEAEA;
}

.not-scheduled {
  background-color: #C1CCD4;
}

.v-data-table__expanded__content td {
  text-align: left;
}
</style>
